import { Base, WarningCircleIcon } from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import {
  ALL_SCOPES,
  SCOPES_GUIDE_URL,
  SCOPE_GROUPS,
  ScopeGroup,
  groupLabel,
  scopeDescription,
  scopeLabel
} from './scope-catalog'

const { Badge, Button, Callout, Card, Checkbox } = Base

interface ScopePickerProps {
  scopes: string[]
  onChange: (scopes: string[]) => void
}

export const ScopePicker: React.FC<ScopePickerProps> = ({
  scopes,
  onChange
}) => {
  const { t } = useTranslation()
  const allSelected = ALL_SCOPES.every((scope) => scopes.includes(scope))

  return (
    <div className="flex flex-col gap-3">
      <Callout tone="info">
        <p className="text-xs leading-relaxed">
          <span className="font-medium">{t('api_keys.scopes.tip_title')}</span>{' '}
          {t('api_keys.scopes.tip_body')}{' '}
          <a
            href={SCOPES_GUIDE_URL}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary-base underline"
          >
            {t('api_keys.scopes.guide_link')}
          </a>
        </p>
      </Callout>

      <Card variant="flat" className="flex flex-col gap-1 p-3">
        {/* Select all lists today's scopes, unlike "*": a scope added later is not granted. */}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(allSelected ? [] : ALL_SCOPES)}
          >
            {allSelected
              ? t('api_keys.scopes.clear')
              : t('api_keys.scopes.select_all')}
          </Button>
        </div>
        <div className="flex max-h-80 flex-col divide-y divide-neutral-200 overflow-y-auto">
          {SCOPE_GROUPS.map((group) => (
            <ScopeGroupItem
              key={group.id}
              group={group}
              scopes={scopes}
              onChange={onChange}
            />
          ))}
        </div>
      </Card>
    </div>
  )
}

const ScopeGroupItem: React.FC<{
  group: ScopeGroup
  scopes: string[]
  onChange: (scopes: string[]) => void
}> = ({ group, scopes, onChange }) => {
  const { t } = useTranslation()
  const groupScopes = group.scopes
  const label = groupLabel(group, t)
  const held = groupScopes.filter((scope) => scopes.includes(scope)).length
  const groupId = `scope-group-${group.id}`

  const setGroup = (checked: boolean) => {
    const others = scopes.filter((scope) => !groupScopes.includes(scope))
    onChange(checked ? [...others, ...groupScopes] : others)
  }
  const setScope = (scope: string, checked: boolean) =>
    onChange(
      checked ? [...scopes, scope] : scopes.filter((other) => other !== scope)
    )

  return (
    <div className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <label
        htmlFor={groupId}
        className="flex cursor-pointer items-center gap-2"
      >
        <Checkbox
          id={groupId}
          size="sm"
          aria-label={label}
          checked={held === groupScopes.length}
          indeterminate={held > 0 && held < groupScopes.length}
          onCheckedChange={setGroup}
        />
        <span className="text-sm font-medium">{label}</span>
        {group.sensitive ? (
          <Badge tone="warning" icon={<WarningCircleIcon />}>
            {t('api_keys.scopes.sensitive')}
          </Badge>
        ) : null}
        <span className="ml-auto text-xs text-neutral-500">
          {held}/{groupScopes.length}
        </span>
      </label>
      <div className="flex flex-col gap-1.5 pl-6">
        {group.scopes.map((scope) => {
          const id = `scope-${scope}`
          return (
            <label
              key={scope}
              htmlFor={id}
              className="flex cursor-pointer items-start gap-2"
            >
              <Checkbox
                id={id}
                size="sm"
                className="mt-0.5"
                aria-label={`${label}: ${scopeLabel(scope, t)}`}
                checked={scopes.includes(scope)}
                onCheckedChange={(next) => setScope(scope, next)}
              />
              <span className="text-sm">
                {scopeLabel(scope, t)}{' '}
                <span className="text-xs text-neutral-500">
                  {scopeDescription(scope, t)}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}
